import { useRef, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Icon } from '../../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, Switch } from '../../components/ui.tsx';
import { t, type MessageKey } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { ACCENTS, DEFAULT_LOOK, DOC_KINDS, logoSrc, type DocKind, type DocTemplate, type Look, type PaperLook } from '../../lib/doclook.tsx';
import { todayIso } from '../../lib/format.ts';
import { shrinkPhoto } from '../../lib/images.ts';
import { useUi } from '../../lib/ui-state.tsx';
import type { Company } from '../../lib/types.ts';
import { InvoicePaper, type Detail } from '../Invoices.tsx';
import { QuotePaper, type QuoteDetail } from '../Quotes.tsx';
import { StatementPaper, type StatementData } from '../Statement.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';

const DOC_LABEL: Record<DocKind, MessageKey> = { invoice: 'look.doc_invoice', quote: 'look.doc_quote', statement: 'look.doc_statement' };
const DOC_ICON = { invoice: 'invoice', quote: 'note', statement: 'list' } as const;
const STYLES: Look['style'][] = ['classic', 'band', 'minimal'];

/**
 * Settings → Document templates: how invoices, quotes and statements look on paper. Each
 * template is the default for some documents; the others can be picked when printing.
 */
export function DocTemplates() {
  const { data } = useAction<DocTemplate[]>('studio.doc_templates');
  const navigate = useNavigate();
  const add = (
    <Button variant="primary" icon="plus" onClick={() => navigate('/manage/documents/new')}>
      {t('look.new')}
    </Button>
  );
  return (
    <ManageLayout title={t('manage.documents')} subtitle={t('look.subtitle')} actions={data?.length ? add : undefined}>
      {!data ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : !data.length ? (
        <EmptyState icon="printer" title={t('look.empty')} body={t('look.empty_body')} action={add} />
      ) : (
        <>
          <div className="look-cards">
            {data.map((x) => (
              <Link key={x.id} to={`/manage/documents/${x.id}`} className="look-card">
                <MiniPaper look={x.look} logo={x.logo} />
                <span className="look-card-main">
                  <span className="list-title">{x.name}</span>
                  <span className="list-sub">{x.documents.length ? t('look.default_for', { docs: x.documents.map((d) => t(DOC_LABEL[d])).join(', ') }) : t('look.when_picked')}</span>
                </span>
                <Icon name="chevronRight" size={18} />
              </Link>
            ))}
          </div>
          {DOC_KINDS.some((d) => !data.some((x) => x.documents.includes(d))) ? (
            <p className="callout callout-info">
              <Icon name="note" size={16} />
              <span>
                {t('look.plain_for', {
                  docs: DOC_KINDS.filter((d) => !data.some((x) => x.documents.includes(d)))
                    .map((d) => t(DOC_LABEL[d]))
                    .join(', '),
                })}
              </span>
            </p>
          ) : null}
        </>
      )}
    </ManageLayout>
  );
}

/** A small drawing of the paper: band, colour and logo. */
function MiniPaper({ look, logo }: { look: Look; logo: string | null }) {
  return (
    <span className={`look-mini look-mini-${look.style}`} style={{ '--look-accent': look.accent } as CSSProperties} aria-hidden="true">
      <span className="look-mini-head">{logo ? <img src={logoSrc(logo)} alt="" /> : <i />}</span>
      <span className="look-mini-rule" />
      <i />
      <i />
      <i className="short" />
    </span>
  );
}

/** One template: what it changes on the left, the paper as it prints on the right. */
export function DocTemplateEditor() {
  const { id } = useParams();
  const isNew = id === 'new';
  const { data: all } = useAction<DocTemplate[]>('studio.doc_templates');
  const { data: company } = useAction<Company>('company.get');
  const was = isNew ? null : (all?.find((x) => x.id === id) ?? null);
  if (!all || !company) {
    return (
      <ManageLayout title={t('manage.documents')}>
        <Skeleton h={480} r="var(--radius-lg)" />
      </ManageLayout>
    );
  }
  if (!isNew && !was) {
    return (
      <ManageLayout title={t('manage.documents')}>
        <EmptyState icon="printer" title={t('look.not_found')} action={<Link to="/manage/documents">{t('look.back')}</Link>} />
      </ManageLayout>
    );
  }
  return <Editor key={id} was={was} all={all} company={company} />;
}

function Editor({ was, all, company }: { was: DocTemplate | null; all: DocTemplate[]; company: Company }) {
  const navigate = useNavigate();
  const { fail } = useUi();
  const { busy, run } = useSave();
  const [name, setName] = useState(was?.name ?? (all.length ? '' : t('look.first_name')));
  const [look, setLook] = useState<Look>(was?.look ?? DEFAULT_LOOK);
  const [logo, setLogo] = useState<string | null>(was?.logo ?? null);
  // The first template is the default for every document, until the person says otherwise.
  const [docs, setDocs] = useState<DocKind[]>(was?.documents ?? (all.length ? [] : [...DOC_KINDS]));
  const [preview, setPreview] = useState<DocKind>('invoice');
  const [tried, setTried] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const set = <K extends keyof Look>(k: K, v: Look[K]) => setLook((l) => ({ ...l, [k]: v }));
  const nameError = !name.trim() ? t('form.required') : all.some((x) => x.id !== was?.id && x.name.toLowerCase() === name.trim().toLowerCase()) ? t('look.name_taken') : null;

  const pickLogo = async (f: File | undefined) => {
    if (!f) return;
    setLogoBusy(true);
    try {
      setLogo((await shrinkPhoto(f, 600, 0.9)).base64);
    } catch {
      fail(new Error(t('look.logo_invalid')));
    } finally {
      setLogoBusy(false);
      if (file.current) file.current.value = '';
    }
  };
  const save = () => {
    setTried(true);
    if (nameError) return;
    void run(async () => {
      await action('studio.save_doc_template', { ...(was ? { id: was.id } : {}), name: name.trim(), look, logo, documents: docs });
      navigate('/manage/documents');
    }, t('look.saved'));
  };
  const remove = () =>
    void run(async () => {
      await action('studio.delete_doc_template', { id: was!.id });
      navigate('/manage/documents');
    }, t('look.deleted'));
  const others = (d: DocKind) => all.find((x) => x.id !== was?.id && x.documents.includes(d));
  const paper: PaperLook = { look, logo };

  return (
    <ManageLayout
      title={was ? was.name : t('look.new')}
      actions={
        <>
          {was ? (
            <Button variant="ghost" icon="trash" onClick={() => setDeleting(true)}>
              {t('common.delete')}
            </Button>
          ) : null}
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {t('look.save')}
          </Button>
        </>
      }
    >
      <nav className="prj-crumbs small" aria-label={t('look.crumbs')}>
        <Link to="/manage/documents">{t('manage.documents')}</Link>
        <Icon name="chevronRight" size={14} />
        <span>{was ? was.name : t('look.new')}</span>
      </nav>
      <div className="look-editor">
        <div className="look-form">
          <section className="look-group">
            <Field label={t('look.name')} error={tried ? nameError : null} required>
              <input className="input" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} data-autofocus />
            </Field>
            <div className="field">
              <span className="field-label">{t('look.use_for')}</span>
              <div className="look-switches">
                {DOC_KINDS.map((d) => (
                  <Toggle key={d} checked={docs.includes(d)} onChange={(on) => setDocs((x) => (on ? [...x, d] : x.filter((y) => y !== d)))} label={t(DOC_LABEL[d])} hint={docs.includes(d) && others(d) ? t('look.takes_from', { name: others(d)!.name }) : undefined} />
                ))}
              </div>
            </div>
          </section>

          <section className="look-group" aria-labelledby="look-logo">
            <h2 className="look-group-title" id="look-logo">
              {t('look.logo')}
            </h2>
            <div className="look-logo-row">
              {logo ? <img className="look-logo-thumb" src={logoSrc(logo)} alt={t('look.logo')} /> : <span className="look-logo-thumb empty" aria-hidden="true"><Icon name="image" size={22} /></span>}
              <div className="row-actions">
                <Button size="sm" icon="image" onClick={() => file.current?.click()} busy={logoBusy}>
                  {logo ? t('look.logo_change') : t('look.logo_add')}
                </Button>
                {logo ? (
                  <Button size="sm" variant="ghost" icon="x" onClick={() => setLogo(null)}>
                    {t('look.logo_remove')}
                  </Button>
                ) : null}
              </div>
              <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => void pickLogo(e.target.files?.[0])} />
            </div>
            {logo ? (
              <div className="look-pair">
                <Segments label={t('look.logo_size')} value={look.logo_size} options={(['s', 'm', 'l'] as const).map((v) => [v, t(`look.size_${v}`)])} onChange={(v) => set('logo_size', v)} />
                <Segments label={t('look.logo_place')} value={look.logo_place} options={(['left', 'right'] as const).map((v) => [v, t(`look.place_${v}`)])} onChange={(v) => set('logo_place', v)} />
              </div>
            ) : (
              <p className="field-hint">{t('look.logo_hint')}</p>
            )}
          </section>

          <section className="look-group" aria-labelledby="look-style">
            <h2 className="look-group-title" id="look-style">
              {t('look.style')}
            </h2>
            <div className="look-styles" role="radiogroup" aria-labelledby="look-style">
              {STYLES.map((s) => (
                <button key={s} type="button" role="radio" aria-checked={look.style === s} className={`look-style${look.style === s ? ' active' : ''}`} onClick={() => set('style', s)}>
                  <MiniPaper look={{ ...look, style: s }} logo={null} />
                  <span>{t(`look.style_${s}`)}</span>
                </button>
              ))}
            </div>
            <div className="field">
              <span className="field-label" id="look-colour">
                {t('look.colour')}
              </span>
              <div className="look-swatches" role="radiogroup" aria-labelledby="look-colour">
                {ACCENTS.map((c) => (
                  <button key={c} type="button" role="radio" aria-checked={look.accent === c} aria-label={c} className={`look-swatch${look.accent === c ? ' active' : ''}`} style={{ background: c }} onClick={() => set('accent', c)}>
                    {look.accent === c ? <Icon name="check" size={16} /> : null}
                  </button>
                ))}
                <label className={`look-swatch look-swatch-own${ACCENTS.includes(look.accent) ? '' : ' active'}`} title={t('look.colour_own')}>
                  <input type="color" value={look.accent} onChange={(e) => set('accent', e.target.value)} aria-label={t('look.colour_own')} />
                  <Icon name="plus" size={16} />
                </label>
              </div>
            </div>
            <Segments label={t('look.font')} value={look.font} options={(['sans', 'serif'] as const).map((v) => [v, t(`look.font_${v}`)])} onChange={(v) => set('font', v)} />
          </section>

          <section className="look-group" aria-labelledby="look-show">
            <h2 className="look-group-title" id="look-show">
              {t('look.what_prints')}
            </h2>
            <div className="look-switches">
              <Toggle checked={look.lines.number} onChange={(v) => set('lines', { ...look.lines, number: v })} label={t('look.line_numbers')} />
              <Toggle checked={look.lines.unit} onChange={(v) => set('lines', { ...look.lines, unit: v })} label={t('look.units')} />
              <Toggle checked={look.show.bank} onChange={(v) => set('show', { ...look.show, bank: v })} label={t('look.bank')} />
              <Toggle checked={look.show.terms} onChange={(v) => set('show', { ...look.show, terms: v })} label={t('look.terms')} />
              <Toggle checked={look.show.issued_by} onChange={(v) => set('show', { ...look.show, issued_by: v })} label={t('look.issued_by')} />
              <Toggle checked={look.show.fields} onChange={(v) => set('show', { ...look.show, fields: v })} label={t('look.fields')} />
              <Toggle checked={look.show.signatures} onChange={(v) => set('show', { ...look.show, signatures: v })} label={t('look.signatures')} />
            </div>
            <p className="callout callout-info look-law">
              <Icon name="shieldCheck" size={16} />
              <span>{t('look.always')}</span>
            </p>
          </section>

          <section className="look-group" aria-labelledby="look-texts">
            <h2 className="look-group-title" id="look-texts">
              {t('look.texts')}
            </h2>
            <Field label={t('look.header_note')} hint={t('look.header_note_hint')} counter={`${(look.header_note ?? '').length}/300`}>
              <input className="input" maxLength={300} value={look.header_note ?? ''} onChange={(e) => set('header_note', e.target.value || null)} />
            </Field>
            <Field label={t('look.footer')} hint={t('look.footer_hint')} counter={`${(look.footer ?? '').length}/500`}>
              <textarea className="input" rows={3} maxLength={500} value={look.footer ?? ''} onChange={(e) => set('footer', e.target.value || null)} />
            </Field>
          </section>
        </div>

        <section className="look-preview" aria-label={t('look.preview')}>
          <div className="look-preview-head">
            <span className="muted small">
              <Icon name="eye" size={14} /> {t('look.preview')}
            </span>
            <div className="segmented subtle" role="radiogroup" aria-label={t('look.preview')}>
              {DOC_KINDS.map((d) => (
                <button key={d} type="button" role="radio" aria-checked={preview === d} className={preview === d ? 'active' : ''} onClick={() => setPreview(d)}>
                  <Icon name={DOC_ICON[d]} size={16} />
                  {t(DOC_LABEL[d])}
                </button>
              ))}
            </div>
          </div>
          <div className="inv-preview look-preview-paper">
            {preview === 'invoice' ? (
              <InvoicePaper d={sampleInvoice(company)} look={paper} sampleFields={[{ label: t('look.sample_field'), text: 'PO-2026-118' }]} />
            ) : preview === 'quote' ? (
              <QuotePaper d={sampleQuote(company)} look={paper} />
            ) : (
              <StatementPaper d={sampleStatement(company)} look={paper} />
            )}
          </div>
          <p className="muted small">{t('look.sample_note')}</p>
        </section>
      </div>
      {deleting && was ? (
        <Dialog
          title={t('look.delete_title', { name: was.name })}
          onClose={() => setDeleting(false)}
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDeleting(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" icon="trash" onClick={remove} busy={busy}>
                {t('common.delete')}
              </Button>
            </>
          }
        >
          <p>{was.documents.length ? t('look.delete_body_default') : t('look.delete_body')}</p>
        </Dialog>
      ) : null}
    </ManageLayout>
  );
}

function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }) {
  return (
    <label className="look-toggle">
      <span>
        <span>{label}</span>
        {hint ? <span className="field-hint">{hint}</span> : null}
      </span>
      <Switch checked={checked} onChange={onChange} label={label} />
    </label>
  );
}

function Segments<V extends string>({ label, value, options, onChange }: { label: string; value: V; options: [V, string][]; onChange: (v: V) => void }) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <div className="segmented subtle" role="radiogroup" aria-label={label}>
        {options.map(([v, text]) => (
          <button key={v} type="button" role="radio" aria-checked={value === v} className={value === v ? 'active' : ''} onClick={() => onChange(v)}>
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Made-up papers for the preview */

const seller = (c: Company) => c;
/** A day some days from another, as YYYY-MM-DD. */
const addDays = (iso: string, n: number) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const buyer = { id: 'sample', name: 'Hotel Drini sh.p.k.', tax_id: 'L91234567A', id_type: 'NUIS', address: 'Rruga e Kavajës 12', town: 'Tiranë', country: 'AL' };
const sampleLines = [
  { id: '1', product: null, name: 'Espresso coffee beans, 1 kg', unit: 'kg' as const, qty: 12, unit_price: 180000, discount: 0, tax_rate: 20, net: 2160000, tax: 432000, total: 2592000, credited: 0 },
  { id: '2', product: null, name: 'Mineral water 0.5 l (24 pack)', unit: 'box' as const, qty: 30, unit_price: 72000, discount: 5, tax_rate: 20, net: 2052000, tax: 410400, total: 2462400, credited: 0 },
  { id: '3', product: null, name: 'Delivery and set-up', unit: 'service' as const, qty: 1, unit_price: 150000, discount: 0, tax_rate: 20, net: 150000, tax: 30000, total: 180000, credited: 0 },
];
const sums = { net: 4362000, tax: 872400, total: 5234400 };

function sampleInvoice(c: Company): Detail {
  const today = todayIso();
  return {
    invoice: { id: 'sample', kind: 'invoice', number: 128, year: Number(today.slice(0, 4)), state: 'issued', customer: 'sample', date: today, due_date: addDays(today, 15), reference: 'PO-2026-118', ...sums, paid: 0, days_overdue: 0, credit_of: null, name: `${t('inv.inv_prefix')} 128/${today.slice(0, 4)}`, location: 'sample', location_name: '', note: null, reason: null, return_goods: false, cancel_reason: null, issued_at: today, issued_by_name: 'Mira Kola', buyer: null, sales_order: null },
    customer: { ...buyer, id_type: 'NUIS', is_company: true, phone: null, email: null, payment_days: 15, credit_limit: null, active: true, balance: { open: 0, overdue: 0 }, problems: [] },
    lines: sampleLines,
    payments: [],
    related: [],
    company: seller(c),
    settings: { payment_days: 15, quote_days: 30, terms: t('look.sample_terms'), bank_accounts: [{ bank: 'Bank', iban: 'AL00 0000 0000 0000 0000 0000 0000', swift: null, currency: 'ALL' }] },
    branch: { name: '', address: null, city: null },
    order: null,
    fiscal: null,
  };
}

function sampleQuote(c: Company): QuoteDetail {
  const today = todayIso();
  return {
    quote: { id: 'sample', number: 42, year: Number(today.slice(0, 4)), name: `${t('quote.q_prefix')} 42/${today.slice(0, 4)}`, state: 'sent', order: false, expired: false, customer: 'sample', date: today, valid_until: addDays(today, 30), delivery_date: null, location: 'sample', location_name: '', reference: null, note: null, ...sums, sent_at: today, confirmed_at: null, closed_reason: null, created_by_name: 'Mira Kola', confirmed_by_name: null, share_token: null, viewed_at: null, accepted_name: null, accepted_at: null },
    lines: sampleLines.map((l) => ({ ...l, invoiced: 0, drafted: 0, delivered: 0, order_line: null })),
    options: [],
    deliveries: [],
    invoices: [],
    customer: { ...buyer, active: true },
    company: seller(c),
    settings: { payment_days: 15, quote_days: 30, terms: t('look.sample_terms'), bank_accounts: [] },
    branch: { name: '', address: null, city: null },
  } as QuoteDetail;
}

function sampleStatement(c: Company): StatementData {
  const today = todayIso();
  const y = today.slice(0, 4);
  const d1 = `${y}-01-01`;
  return {
    customer: buyer,
    company: seller(c),
    from: d1,
    to: today,
    opening: 0,
    entries: [
      { kind: 'invoice', date: addDays(today, -40), document: 'x', doc_kind: 'invoice', number: 97, year: Number(y), ref: null, due_date: addDays(today, -25), method: null, debit: 3120000, credit: 0, balance: 3120000 },
      { kind: 'payment', date: addDays(today, -20), document: 'x', doc_kind: 'invoice', number: 97, year: Number(y), ref: null, due_date: null, method: 'bank', debit: 0, credit: 3120000, balance: 0 },
      { kind: 'invoice', date: today, document: 'x', doc_kind: 'invoice', number: 128, year: Number(y), ref: 'PO-2026-118', due_date: addDays(today, 15), method: null, debit: sums.total, credit: 0, balance: sums.total },
    ],
    debits: 3120000 + sums.total,
    credits: 3120000,
    closing: sums.total,
    ages: { current: sums.total, d30: 0, d60: 0, d90: 0, older: 0 },
    open: [{ id: 'x', kind: 'invoice', number: 128, year: Number(y), date: today, due_date: addDays(today, 15), total: sums.total, open: sums.total, days_late: 0 }],
  } as StatementData;
}
